import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { getCurrentUserProfile, getRewards, redeemReward, getRedemptionHistory } from '../services/api';
import Toast from './Toast';
import useToast from '../hooks/useToast';

const RewardsPage = () => {
  const { t, i18n } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [filteredRewards, setFilteredRewards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [eligibilityFilter, setEligibilityFilter] = useState('all');
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redemptionNotes, setRedemptionNotes] = useState('');
  const [activeTab, setActiveTab] = useState('rewards'); // 'rewards' or 'history'
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [redeeming, setRedeeming] = useState(false);
  const { toast, showSuccess, showError, showWarning, hideToast } = useToast();

  // Mock rewards data
  const mockRewards = [
    // Monetary rewards
    {
      id: 1,
      name: '$25 Gift Card',
      description: 'Redeem for a $25 gift card to your favorite store or restaurant.',
      pointsCost: 50,
      category: 'monetary',
      eligibility: {
        minPoints: 50,
        roles: null, // Available to all roles
        availability: 'available'
      },
      icon: 'card_giftcard'
    },
    {
      id: 2,
      name: '$50 Gift Card',
      description: 'Redeem for a $50 gift card to your favorite store or restaurant.',
      pointsCost: 100,
      category: 'monetary',
      eligibility: {
        minPoints: 100,
        roles: null,
        availability: 'available'
      },
      icon: 'card_giftcard'
    },
    {
      id: 3,
      name: '$100 Gift Card',
      description: 'Redeem for a $100 gift card to your favorite store or restaurant.',
      pointsCost: 200,
      category: 'monetary',
      eligibility: {
        minPoints: 200,
        roles: null,
        availability: 'limited'
      },
      icon: 'card_giftcard'
    },
    // Training rewards
    {
      id: 4,
      name: 'Training Priority Access',
      description: 'Get priority access to advanced training courses and workshops. Skip the waiting list!',
      pointsCost: 75,
      category: 'training',
      eligibility: {
        minPoints: 75,
        roles: null,
        availability: 'available'
      },
      icon: 'school'
    },
    {
      id: 5,
      name: 'Certification Course Voucher',
      description: 'Free access to a professional certification course of your choice.',
      pointsCost: 150,
      category: 'training',
      eligibility: {
        minPoints: 150,
        roles: null,
        availability: 'available'
      },
      icon: 'workspace_premium'
    },
    // Recognition rewards
    {
      id: 6,
      name: 'Employee Spotlight',
      description: 'Be featured in the monthly employee spotlight newsletter and company communications.',
      pointsCost: 80,
      category: 'recognition',
      eligibility: {
        minPoints: 80,
        roles: null,
        availability: 'available'
      },
      icon: 'star'
    },
    {
      id: 7,
      name: 'Recognition Certificate',
      description: 'Receive an official recognition certificate signed by management for your achievements.',
      pointsCost: 60,
      category: 'recognition',
      eligibility: {
        minPoints: 60,
        roles: null,
        availability: 'available'
      },
      icon: 'emoji_events'
    },
    // Privileges rewards
    {
      id: 8,
      name: 'Flexible Work Hours',
      description: 'Enjoy flexible work hours for one month. Choose your preferred start and end times.',
      pointsCost: 90,
      category: 'privileges',
      eligibility: {
        minPoints: 90,
        roles: ['EMPLOYE'],
        availability: 'available'
      },
      icon: 'schedule'
    },
    {
      id: 9,
      name: 'Parking Spot Priority',
      description: 'Get priority access to premium parking spots for one month.',
      pointsCost: 70,
      category: 'privileges',
      eligibility: {
        minPoints: 70,
        roles: null,
        availability: 'limited'
      },
      icon: 'local_parking'
    },
    {
      id: 10,
      name: 'Extra Vacation Day',
      description: 'Redeem an extra paid vacation day to use at your convenience.',
      pointsCost: 120,
      category: 'privileges',
      eligibility: {
        minPoints: 120,
        roles: null,
        availability: 'available'
      },
      icon: 'flight_takeoff'
    },
    {
      id: 11,
      name: 'Lunch with Manager',
      description: 'Enjoy a complimentary lunch with your manager to discuss career growth and opportunities.',
      pointsCost: 40,
      category: 'privileges',
      eligibility: {
        minPoints: 40,
        roles: ['EMPLOYE'],
        availability: 'available'
      },
      icon: 'restaurant'
    },
    {
      id: 12,
      name: 'Work from Home Week',
      description: 'Work from home for an entire week. Perfect for better work-life balance.',
      pointsCost: 110,
      category: 'privileges',
      eligibility: {
        minPoints: 110,
        roles: null,
        availability: 'available'
      },
      icon: 'home'
    }
  ];

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoadingUser(true);
        const userData = await getCurrentUserProfile();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        setCurrentUser({
          total_points: 0,
          role: 'EMPLOYE'
        });
      } finally {
        setLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch rewards from API (fallback to mock data if API unavailable)
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const response = await getRewards();
        if (response && (response.results || (Array.isArray(response) && response.length > 0))) {
          // Use API data
          const apiRewards = response.results || response;
          const userPoints = currentUser?.total_points || 0;
          const userRole = currentUser?.role || 'EMPLOYE';

          // Transform API rewards to match frontend format
          const rewardsWithEligibility = apiRewards.map(reward => {
            const pointsCost = reward.points_cost || 0;
            const minPointsRequired = reward.min_points_required || null;
            const hasEnoughPoints = userPoints >= pointsCost;
            const hasMinPoints = !minPointsRequired || userPoints >= minPointsRequired;
            const isEligible = hasEnoughPoints && hasMinPoints;

            return {
              id: reward.id,
              name: reward.name,
              description: reward.description,
              pointsCost: pointsCost,
              category: reward.category,
              icon: reward.icon || 'card_giftcard',
              eligibility: {
                minPoints: pointsCost, // Cost to redeem
                minPointsRequired: minPointsRequired, // Minimum points to see/access this reward
                roles: null, // All employees can see rewards
                availability: reward.is_active ? 'available' : 'unavailable',
                isGlobal: reward.is_global !== false,
                applicableTeams: reward.applicable_teams || []
              },
              isEligible,
              requires_approval: reward.requires_approval !== false
            };
          });

          setRewards(rewardsWithEligibility);
        } else {
          // Fallback to mock data
          const userPoints = currentUser?.total_points || 0;
          const userRole = currentUser?.role || 'EMPLOYE';

          const rewardsWithEligibility = mockRewards.map(reward => {
            const hasEnoughPoints = userPoints >= reward.eligibility.minPoints;
            const hasRightRole = !reward.eligibility.roles || reward.eligibility.roles.includes(userRole);
            const isAvailable = reward.eligibility.availability !== 'unavailable';
            
            const isEligible = hasEnoughPoints && hasRightRole && isAvailable;

            return {
              ...reward,
              isEligible,
              requires_approval: true // All rewards require approval
            };
          });

          setRewards(rewardsWithEligibility);
        }
      } catch (error) {
        console.error('Error fetching rewards:', error);
        // Fallback to mock data on error
        const userPoints = currentUser?.total_points || 0;
        const userRole = currentUser?.role || 'EMPLOYE';

        const rewardsWithEligibility = mockRewards.map(reward => {
          const hasEnoughPoints = userPoints >= reward.eligibility.minPoints;
          const hasRightRole = !reward.eligibility.roles || reward.eligibility.roles.includes(userRole);
          const isAvailable = reward.eligibility.availability !== 'unavailable';
          
          const isEligible = hasEnoughPoints && hasRightRole && isAvailable;

          return {
            ...reward,
            isEligible,
            requires_approval: true
          };
        });

        setRewards(rewardsWithEligibility);
      }
    };

    if (currentUser) {
      fetchRewards();
    }
  }, [currentUser]);

  // Recalculate eligibility when user points change (for existing rewards)
  useEffect(() => {
    if (rewards.length > 0 && currentUser) {
      const userPoints = currentUser?.total_points || 0;
      const userRole = currentUser?.role || 'EMPLOYE';

      setRewards(prevRewards => prevRewards.map(reward => {
        // Handle both API format (points_cost) and mock format (pointsCost)
        const pointsCost = reward.points_cost || reward.pointsCost || 0;
        const minPointsRequired = reward.eligibility?.minPointsRequired || reward.min_points_required || null;
        const minPoints = reward.eligibility?.minPoints || pointsCost;
        
        const hasEnoughPoints = userPoints >= minPoints;
        const hasMinPointsRequired = !minPointsRequired || userPoints >= minPointsRequired;
        const hasRightRole = !reward.eligibility?.roles || reward.eligibility.roles.includes(userRole);
        const isAvailable = reward.eligibility?.availability !== 'unavailable';
        
        const isEligible = hasEnoughPoints && hasMinPointsRequired && hasRightRole && isAvailable;

        return {
          ...reward,
          isEligible,
          pointsCost: pointsCost // Normalize to pointsCost
        };
      }));
    }
  }, [currentUser?.total_points, currentUser?.role]);

  // Filter rewards based on search, category, and eligibility
  useEffect(() => {
    let filtered = [...rewards];

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(reward =>
        reward.name.toLowerCase().includes(term) ||
        reward.description.toLowerCase().includes(term)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(reward => reward.category === selectedCategory);
    }

    // Filter by eligibility
    if (eligibilityFilter === 'eligible') {
      filtered = filtered.filter(reward => reward.isEligible);
    } else if (eligibilityFilter === 'not-eligible') {
      filtered = filtered.filter(reward => !reward.isEligible);
    }

    setFilteredRewards(filtered);
  }, [rewards, searchTerm, selectedCategory, eligibilityFilter]);

  const categories = [
    { value: 'all', label: t('rewards.allCategories') },
    { value: 'monetary', label: t('rewards.monetary') },
    { value: 'training', label: t('rewards.training') },
    { value: 'recognition', label: t('rewards.recognition') },
    { value: 'privileges', label: t('rewards.privileges') }
  ];

  const getAvailabilityBadge = (reward) => {
    if (!reward.isEligible) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80">
          <span className="material-symbols-outlined text-[14px]">block</span>
          {t('rewards.notEligible')}
        </span>
      );
    }

    if (reward.eligibility.availability === 'limited') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70">
          <span className="material-symbols-outlined text-[14px]">warning</span>
          {t('rewards.limitedStock')}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70">
        <span className="material-symbols-outlined text-[14px]">check_circle</span>
        {t('rewards.available')}
      </span>
    );
  };

  const getEligibilityMessage = (reward) => {
    const userPoints = currentUser?.total_points || 0;
    const userRole = currentUser?.role || 'EMPLOYE';
    const minPointsRequired = reward.eligibility?.minPointsRequired || reward.min_points_required || null;
    const pointsCost = reward.pointsCost || reward.points_cost || 0;

    // Check minimum points required to see/access this reward
    if (minPointsRequired && userPoints < minPointsRequired) {
      const pointsNeeded = minPointsRequired - userPoints;
      return t('rewards.needMoreToAccessMsg', { needed: pointsNeeded, min: minPointsRequired });
    }

    // Check if user has enough points to redeem
    if (userPoints < pointsCost) {
      const pointsNeeded = pointsCost - userPoints;
      return t('rewards.needMoreToRedeem', { needed: pointsNeeded, cost: pointsCost });
    }

    // Check role eligibility
    if (reward.eligibility?.roles && !reward.eligibility.roles.includes(userRole)) {
      return t('rewards.roleOnly', { roles: reward.eligibility.roles.join(' or ') });
    }

    // Check team visibility (if not global and user's team not in applicable teams)
    if (!reward.eligibility?.isGlobal && reward.eligibility?.applicableTeams) {
      const userTeamId = currentUser?.team_id || currentUser?.team?.id;
      const applicableTeamIds = reward.eligibility.applicableTeams.map(t => t.id || t);
      if (userTeamId && !applicableTeamIds.includes(userTeamId)) {
        return t('rewards.notForTeam');
      }
    }

    // Check availability
    if (reward.eligibility?.availability === 'unavailable') {
      return t('rewards.currentlyUnavailable');
    }

    // Check if approval is required
    if (reward.requires_approval) {
      return t('rewards.eligibleWithApproval');
    }

    return t('rewards.eligible');
  };

  // Fetch redemption history when tab is active or filter changes
  useEffect(() => {
    const fetchRedemptionHistory = async () => {
      setLoadingHistory(true);
      try {
        const filters = {};
        if (activeTab === 'history' && historyStatusFilter !== 'all') {
          filters.status = historyStatusFilter;
        }
        // Always fetch all redemptions to calculate available points correctly
        const response = await getRedemptionHistory(filters);
        const allRedemptions = response.results || response;
        setRedemptionHistory(allRedemptions);
      } catch (error) {
        console.error('Error fetching redemption history:', error);
        setRedemptionHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    if (currentUser) {
      fetchRedemptionHistory();
    }
  }, [activeTab, historyStatusFilter, currentUser]);

  const handleRedeemClick = (reward) => {
    // Balance verification before showing modal
    const pointsCost = reward.pointsCost || reward.points_cost || 0;
    
    // Check if user has enough available points (total - pending - approved)
    if (availablePoints < pointsCost) {
      const pointsNeeded = pointsCost - availablePoints;
      showWarning(t('rewards.insufficientAvailable'), `You need ${pointsNeeded} more available point${pointsNeeded !== 1 ? 's' : ''} to redeem this reward. You have ${availablePoints} available points (${userPoints} total - ${pendingRedemptionsPoints} pending - ${approvedRedemptionsPoints} approved).`, 6000);
      return;
    }

    // Also check minimum points required if set
    const minPointsRequired = reward.eligibility?.minPointsRequired || reward.min_points_required || null;
    if (minPointsRequired && userPoints < minPointsRequired) {
      const pointsNeeded = minPointsRequired - userPoints;
      showWarning(t('rewards.minimumPointsRequired'), `You need ${pointsNeeded} more point${pointsNeeded !== 1 ? 's' : ''} to access this reward (minimum ${minPointsRequired} points required).`, 5000);
      return;
    }

    setSelectedReward(reward);
    setRedemptionNotes('');
    setShowRedeemModal(true);
  };

  const handleRedeemConfirm = async () => {
    if (!selectedReward) return;

    setRedeeming(true);
    try {
      const rewardId = selectedReward.id;
      const response = await redeemReward(rewardId, redemptionNotes);
      
      // Refresh user profile to get updated points
      const updatedUser = await getCurrentUserProfile();
      setCurrentUser(updatedUser);
      
      // Refresh redemption history to update available points calculation
      try {
        const response = await getRedemptionHistory({});
        setRedemptionHistory(response.results || response || []);
      } catch (error) {
        console.error('Error refreshing redemption history:', error);
      }
      
      // Show success message
      showSuccess(t('rewards.redemptionSubmitted'), response.message || t('rewards.redemptionSubmittedMsg'));
      
      // Close modal
      setShowRedeemModal(false);
      setSelectedReward(null);
      setRedemptionNotes('');
    } catch (error) {
      console.error('Error redeeming reward:', error);
      showError(t('rewards.redemptionFailed'), error.data?.error || error.message || t('rewards.redeemFailedMsg'));
      
      // Show API warnings if present
      if (error.response?.status === 400) {
        showWarning(t('rewards.invalidRequest'), t('rewards.invalidRequestMsg'), 5000);
      } else if (error.response?.status === 403) {
        showWarning(t('imputations.permissionDenied'), t('rewards.permissionDeniedRedeem'), 5000);
      }
    } finally {
      setRedeeming(false);
    }
  };

  // Calculate pending and approved redemptions points
  const pendingRedemptionsPoints = redemptionHistory
    .filter(redemption => redemption.status === 'pending')
    .reduce((sum, redemption) => sum + (redemption.points_cost || 0), 0);

  const approvedRedemptionsPoints = redemptionHistory
    .filter(redemption => redemption.status === 'approved')
    .reduce((sum, redemption) => sum + (redemption.points_cost || 0), 0);

  const userPoints = currentUser?.total_points || 0;
  const availablePoints = userPoints - pendingRedemptionsPoints - approvedRedemptionsPoints;

  const getStatusBadge = (status) => {
    const badges = {
      pending: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700/70">
          <span className="material-symbols-outlined text-[14px]">schedule</span>
          {t('rewards.pending')}
        </span>
      ),
      approved: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70">
          <span className="material-symbols-outlined text-[14px]">check_circle</span>
          {t('rewards.approved')}
        </span>
      ),
      rejected: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70">
          <span className="material-symbols-outlined text-[14px]">cancel</span>
          {t('rewards.rejected')}
        </span>
      )
    };
    return badges[status?.toLowerCase()] || badges.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return tCommon('na');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(getDateLocale(i18n.language), { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Filter redemptions for display based on active tab and status filter
  const displayedRedemptions = activeTab === 'history' && historyStatusFilter !== 'all'
    ? redemptionHistory.filter(r => r.status === historyStatusFilter)
    : activeTab === 'history'
    ? redemptionHistory
    : []; // Don't show redemptions when on rewards tab (they're used for calculations only)

  return (
    <div className="flex flex-col gap-6">
      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />

      {/* Header Section with Points Display */}
      <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800 rounded-xl border border-yellow-200 dark:border-slate-600 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-2">{t('rewards.catalogTitle')}</h1>
            <p className="text-slate-600 dark:text-slate-400 text-base">
              {t('rewards.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3 px-6 py-4 rounded-lg bg-white dark:bg-slate-800 border border-yellow-200 dark:border-slate-600 shadow-sm">
              <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400 text-2xl">stars</span>
              <div className="flex flex-col">
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('rewards.totalPoints')}</span>
                <span className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">{userPoints}</span>
              </div>
            </div>
            {pendingRedemptionsPoints > 0 && (
              <div className="flex items-center gap-3 px-6 py-4 rounded-lg bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-700/50 shadow-sm">
                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-2xl">pending</span>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('rewards.pending')}</span>
                  <span className="text-2xl font-bold text-amber-700 dark:text-amber-400">{pendingRedemptionsPoints}</span>
                </div>
              </div>
            )}
            {approvedRedemptionsPoints > 0 && (
              <div className="flex items-center gap-3 px-6 py-4 rounded-lg bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-700/50 shadow-sm">
                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-2xl">check_circle</span>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('rewards.approved')}</span>
                  <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">{approvedRedemptionsPoints}</span>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 px-6 py-4 rounded-lg bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-700/50 shadow-sm">
              <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400 text-2xl">account_balance_wallet</span>
              <div className="flex flex-col">
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('rewards.available')}</span>
                <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{availablePoints}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm">
        <div className="flex border-b border-slate-200 dark:border-slate-600">
          <button
            onClick={() => setActiveTab('rewards')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'rewards'
                ? 'text-blue-600 dark:text-primary border-b-2 border-blue-600 dark:border-primary'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {t('rewards.availableRewards')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'text-blue-600 dark:text-primary border-b-2 border-blue-600 dark:border-primary'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {t('rewards.myRedemptions')}
          </button>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'rewards' ? (
        <>
      {/* Filters Section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[20px]">
                search
              </span>
              <input
                type="text"
                placeholder={t('rewards.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-slate-700 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="md:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Eligibility Filter */}
          <div className="md:w-48">
            <select
              value={eligibilityFilter}
              onChange={(e) => setEligibilityFilter(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
            >
              <option value="all">{t('rewards.allRewards')}</option>
              <option value="eligible">{t('rewards.eligibleOnly')}</option>
              <option value="not-eligible">{t('rewards.notEligibleFilter')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rewards Grid */}
      {filteredRewards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredRewards.map((reward) => (
              <motion.div
                key={reward.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={`bg-white dark:bg-slate-800 rounded-xl border-2 shadow-sm p-6 flex flex-col gap-4 transition-all hover:shadow-md hover:scale-[1.02] ${
                  reward.isEligible
                    ? 'border-emerald-200 hover:border-emerald-300 dark:border-emerald-700/50 dark:hover:border-emerald-600'
                    : 'border-slate-200 dark:border-slate-600 opacity-75'
                }`}
              >
                {/* Icon and Status Badge */}
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-lg ${
                    reward.category === 'monetary' ? 'bg-blue-50 dark:bg-blue-900/30' :
                    reward.category === 'training' ? 'bg-purple-50 dark:bg-purple-900/30' :
                    reward.category === 'recognition' ? 'bg-yellow-50 dark:bg-yellow-900/30' :
                    'bg-green-50 dark:bg-green-900/30'
                  }`}>
                    <span className={`material-symbols-outlined text-3xl ${
                      reward.category === 'monetary' ? 'text-blue-600 dark:text-blue-400' :
                      reward.category === 'training' ? 'text-purple-600 dark:text-purple-400' :
                      reward.category === 'recognition' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>
                      {reward.icon}
                    </span>
                  </div>
                  {getAvailabilityBadge(reward)}
                </div>

                {/* Reward Name */}
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{reward.name}</h3>

                {/* Description */}
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed flex-1">
                  {reward.description}
                </p>

                {/* Points Cost */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400 text-[18px]">stars</span>
                    <span className="text-lg font-bold text-slate-900 dark:text-slate-100">{reward.pointsCost}</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{tCommon('points')}</span>
                  </div>
                </div>

                {/* Eligibility Info */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 text-xs text-slate-600 dark:text-slate-400">
                  <div className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-[16px] mt-0.5">info</span>
                    <div className="flex-1">
                      <p className="font-medium mb-1 dark:text-slate-300">{t('rewards.eligibilityConditions')}</p>
                      <ul className="space-y-1 text-slate-500 dark:text-slate-400">
                        {reward.eligibility?.minPointsRequired && (
                          <li>• {t('rewards.minPointsAccess', { count: reward.eligibility.minPointsRequired })}</li>
                        )}
                        <li>• {t('rewards.costsPoints', { count: reward.pointsCost })}</li>
                        {reward.requires_approval && (
                          <li>• {t('rewards.managerApprovalRequired')}</li>
                        )}
                        {!reward.eligibility?.isGlobal && reward.eligibility?.applicableTeams?.length > 0 && (
                          <li>• {t('rewards.teamSpecific')}</li>
                        )}
                        {reward.eligibility?.isGlobal && (
                          <li>• {t('rewards.availableAllTeams')}</li>
                        )}
                        {reward.eligibility?.roles && (
                          <li>• {t('rewards.availableForRoles', { roles: reward.eligibility.roles.join(', ') })}</li>
                        )}
                        {!reward.eligibility?.roles && (
                          <li>• {t('rewards.availableAllEmployees')}</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Redeem Button */}
                <button
                  onClick={() => handleRedeemClick(reward)}
                  disabled={!reward.isEligible}
                  className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
                    reward.isEligible
                      ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-primary dark:hover:bg-primary/90 shadow-sm hover:shadow-md'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                  }`}
                  title={!reward.isEligible ? getEligibilityMessage(reward) : t('rewards.clickToRedeem')}
                >
                  {reward.isEligible ? t('rewards.redeemNow') : t('rewards.notEligible')}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm p-12 text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-500 mb-4">search_off</span>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('rewards.noRewardsFound')}</h3>
          <p className="text-slate-600 dark:text-slate-400">
            {t('rewards.adjustFilters')}
          </p>
        </div>
      )}
        </>
      ) : (
        <>
          {/* Redemption History */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('rewards.historyTitle')}</h2>
              <div className="md:w-48">
                <select
                  value={historyStatusFilter}
                  onChange={(e) => setHistoryStatusFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white dark:bg-slate-700 dark:text-slate-100"
                >
                  <option value="all">{t('rewards.allStatus')}</option>
                  <option value="pending">{t('rewards.pending')}</option>
                  <option value="approved">{t('rewards.approved')}</option>
                  <option value="rejected">{t('rewards.rejected')}</option>
                </select>
              </div>
            </div>

            {loadingHistory ? (
              <div className="text-center py-8">
                <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-500 animate-spin">hourglass_empty</span>
                <p className="text-slate-500 dark:text-slate-400 mt-2">{t('rewards.loadingHistory')}</p>
              </div>
            ) : displayedRedemptions.length > 0 ? (
              <div className="space-y-4">
                {displayedRedemptions.map((redemption) => (
                  <motion.div
                    key={redemption.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 p-4 md:p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4 mb-3">
                          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                            <span className="material-symbols-outlined text-2xl text-blue-600 dark:text-blue-400">
                              {redemption.reward?.icon || 'card_giftcard'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                              {redemption.reward?.name || t('rewards.unknownReward')}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                              {redemption.reward?.description || ''}
                            </p>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                              <span className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-[16px]">stars</span>
                                {redemption.points_cost || 0} points
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-[16px]">calendar_today</span>
                                {t('rewards.requested', { date: formatDate(redemption.requested_at || redemption.requested_date || redemption.created_at) })}
                              </span>
                              {redemption.processed_at && (
                                <span className="flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[16px]">
                                    {redemption.status === 'approved' ? 'check_circle' : 'cancel'}
                                  </span>
                                  {redemption.status === 'approved' ? t('rewards.approvedOn', { date: formatDate(redemption.processed_at) }) : t('rewards.rejectedOn', { date: formatDate(redemption.processed_at) })}
                                </span>
                              )}
                            </div>
                            {redemption.processed_by_name && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                {redemption.status === 'approved' ? t('rewards.approvedBy', { name: redemption.processed_by_name }) : t('rewards.rejectedBy', { name: redemption.processed_by_name })}
                              </p>
                            )}
                            {redemption.notes && (
                              <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-600">
                                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{t('rewards.yourNotes')}</p>
                                <p className="text-sm text-slate-700 dark:text-slate-300">{redemption.notes}</p>
                              </div>
                            )}
                            {redemption.manager_notes && (
                              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-700/50">
                                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">{t('rewards.managerNotes')}</p>
                                <p className="text-sm text-blue-800 dark:text-blue-200">{redemption.manager_notes}</p>
                              </div>
                            )}
                            {redemption.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 rounded border border-red-200 dark:border-red-700/50">
                                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">{t('rewards.rejectionReason')}</p>
                                <p className="text-sm text-red-800 dark:text-red-200">{redemption.rejection_reason}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="md:ml-4">
                        {getStatusBadge(redemption.status)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 p-12 text-center">
                <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-500 mb-4">history</span>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('rewards.noRedemptionsYet')}</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  {activeTab === 'history' && historyStatusFilter !== 'all' 
                    ? t('rewards.noStatusRedemptions', { status: historyStatusFilter })
                    : t('rewards.browseHint')}
                </p>
                {activeTab === 'history' && historyStatusFilter === 'all' && (
                  <button
                    onClick={() => setActiveTab('rewards')}
                    className="px-4 py-2 bg-blue-600 dark:bg-primary text-white rounded-lg hover:bg-blue-700 dark:hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    {t('rewards.browseRewards')}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Redeem Confirmation Modal */}
      <AnimatePresence>
        {showRedeemModal && selectedReward && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRedeemModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-600"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('rewards.confirmRedemption')}</h3>
                <button
                  onClick={() => setShowRedeemModal(false)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  <span className="material-symbols-outlined text-[24px]">close</span>
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30">
                    <span className="material-symbols-outlined text-3xl text-blue-600 dark:text-blue-400">
                      {selectedReward.icon}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{selectedReward.name}</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{selectedReward.description}</p>
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t('rewards.pointsCost')}</span>
                    <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                      {selectedReward.pointsCost || selectedReward.points_cost || 0} points
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t('rewards.yourCurrentPoints')}</span>
                    <span className="text-lg font-bold text-yellow-700 dark:text-yellow-400">{userPoints} points</span>
                  </div>
                  {pendingRedemptionsPoints > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 dark:text-slate-400">{t('rewards.pendingRedemptions')}</span>
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-400">-{pendingRedemptionsPoints} points</span>
                    </div>
                  )}
                  {approvedRedemptionsPoints > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 dark:text-slate-400">{t('rewards.approvedRedemptions')}</span>
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-400">-{approvedRedemptionsPoints} points</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 dark:border-slate-600 pt-2 mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('rewards.availablePoints')}</span>
                      <span className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{availablePoints} points</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('rewards.pointsAfter')}</span>
                      <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {availablePoints - (selectedReward.pointsCost || selectedReward.points_cost || 0)} points
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t('rewards.pointsReservedNote')}
                    </p>
                  </div>
                </div>

                {/* Notes Field */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('rewards.optionalNotes')}
                  </label>
                  <textarea
                    value={redemptionNotes}
                    onChange={(e) => setRedemptionNotes(e.target.value)}
                    placeholder={t('rewards.notesPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none bg-white dark:bg-slate-700 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                  />
                </div>

                {/* Approval Notice */}
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700/50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-[18px] mt-0.5">info</span>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">{t('rewards.approvalNoticeTitle')}</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {t('rewards.approvalNoticeBody')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRedeemModal(false);
                    setRedemptionNotes('');
                  }}
                  disabled={redeeming}
                  className="flex-1 py-2.5 px-4 rounded-lg font-medium text-sm border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tCommon('cancel')}
                </button>
                <button
                  onClick={handleRedeemConfirm}
                  disabled={redeeming}
                  className="flex-1 py-2.5 px-4 rounded-lg font-medium text-sm bg-blue-600 dark:bg-primary text-white hover:bg-blue-700 dark:hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {t('rewards.submitRequest')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RewardsPage;

